/**
 * Bridge between the @quotech/auth store and the
 * @quotech/ui diagnostic-capture reporter so each hub's main.tsx
 * is a single-line install.
 *
 * The reporter wants two callbacks:
 *  - getAuthHeaders() — to attribute the row to the operator's user
 *  - getOrganisationId() — to attribute the row to their org
 *
 * Both read from the Zustand auth store. Both are designed to be
 * safe to call DURING a crash — they never throw and never trigger
 * a re-render.
 */

import { useAuthStore } from './authStore'

/**
 * Synchronously read the Authorization header from the auth store.
 * Returns an empty object when the operator is unauthenticated so
 * the reporter can POST anonymously without a header.
 */
export function getAuthHeadersForDiagnostics(): Record<string, string> {
  try {
    const state = useAuthStore.getState()
    const token = (state as { accessToken?: string | null }).accessToken
    if (token) {
      return { Authorization: `Bearer ${token}` }
    }
  } catch {
    /* store unavailable — fall through */
  }
  return {}
}

/**
 * Best-effort organisation id resolution. Reads the `currentOrgId`
 * or `organisation` slice off the auth store — different hubs store
 * it under slightly different keys, hence the defensive lookups.
 */
export function getOrganisationIdForDiagnostics(): number | string | null | undefined {
  try {
    // Defensive read: the shape of the auth store has drifted across
    // tenants (some carry `currentOrgId`, some `organisationId`, some
    // nest the org under `user.organisation_id`). Cast through
    // `unknown` to opt out of structural typing for this best-effort
    // lookup — the runtime checks below validate every read.
    const state = useAuthStore.getState() as unknown as Record<string, unknown>
    const direct = state['currentOrgId'] ?? state['organisationId']
    if (typeof direct === 'number' || typeof direct === 'string') return direct
    const org = state['organisation'] ?? state['currentOrganisation']
    if (org && typeof org === 'object') {
      const id = (org as { id?: number | string }).id
      if (typeof id === 'number' || typeof id === 'string') return id
    }
    const user = state['user']
    if (user && typeof user === 'object') {
      const orgId = (user as { organisation_id?: number | string }).organisation_id
      if (typeof orgId === 'number' || typeof orgId === 'string') return orgId
    }
  } catch {
    /* ignore */
  }
  return undefined
}
